import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    CreditCard,
    Receipt,
    Wallet,
    Truck,
    Info,
    Building2,
    ShieldCheck,
    Loader2,
    Send,
    CheckCircle2,
    Store
} from 'lucide-react';
import Card from '../components/ui/Card';
import Button from '../components/ui/Button';
import Input from '../components/ui/Input';
import SearchableSelectBox from '../components/ui/SearchableSelectBox';
import Modal from '../components/ui/Modal';
import Checkbox from '../components/ui/Checkbox';
import { useToast } from '../hooks/useToast';
import {
    fetchVendors,
    fetchBankList,
    fetchStoresWhereStaffAssigned,
    fetchPocketBalance,
    client,
    fetchUtilClientProductionOrders,
    generateUniquePoNumber,
    fetchPaystackBanksList,
    fetchUtilExpenseAccounts,
} from '../apiHelper';

const WalletExpenses = () => {
    const navigate = useNavigate();
    const { showToast } = useToast();

    // Tab state: 'vendors' or 'daily'
    const [activeTab, setActiveTab] = useState('vendors');

    // Store Selection State
    const [stores, setStores] = useState([]);
    const [selectedStore, setSelectedStore] = useState('');
    const [walletBalance, setWalletBalance] = useState(0);

    // Authentication & Vendor Data State
    const [user, setUser] = useState(null);
    const [vendors, setVendors] = useState([]);
    const [vendorDetails, setVendorDetails] = useState(null);
    const [header, setHeader] = useState({ vendor_id: '' });

    // Bank Account Verification State
    const [bankOptions, setBankOptions] = useState([]);
    const [bankCode, setBankCode] = useState('');
    const [accountNumber, setAccountNumber] = useState('');
    const [verifying, setVerifying] = useState(false);
    const [verificationData, setVerificationData] = useState(null);

    // Payment Form State
    const [amount, setAmount] = useState('');
    const [narration, setNarration] = useState('');
    const [initiating, setInitiating] = useState(false);
    const [transferData, setTransferData] = useState(null); // stores reference/transfer response

    // OTP Modal State
    const [isOtpModalOpen, setIsOtpModalOpen] = useState(false);
    const [otpCode, setOtpCode] = useState('');
    const [confirmingOtp, setConfirmingOtp] = useState(false);

    const [productionOrders, setProductionOrders] = useState([]);
    const [isJobTied, setIsJobTied] = useState(false);
    const [selectedProductionOrderId, setSelectedProductionOrderId] = useState('');
    const [expenseAccounts, setExpenseAccounts] = useState([]);
    const [selectedExpenseAccount, setSelectedExpenseAccount] = useState('');
    const resetForm = () => {
        setVerificationData(null);
        setBankCode('');
        setAccountNumber('');
        setAmount('');
        setNarration('');
        setHeader({ vendor_id: '' });
        setVendorDetails(null);
        setIsJobTied(false);
        setSelectedProductionOrderId('');
        setSelectedExpenseAccount('');
    };


    // 1. Initialize User & Load Vendors + Banks
    useEffect(() => {
        const lcUser = localStorage.getItem("user");
        if (!lcUser) {
            navigate('/login');
            return;
        }
        const parsedUser = JSON.parse(lcUser);
        setUser(parsedUser);

        const loadInitialData = async () => {
            try {
                // Fetch vendor list using utility
                const vList = await fetchVendors(parsedUser);
                setVendors(Array.isArray(vList) ? vList : []);

                // Fetch assigned store / branches
                const returnedStores = await fetchStoresWhereStaffAssigned(parsedUser);
                const fetchedStores = (returnedStores || []).filter(store => store.storetype !== 'WHS');

                if (fetchedStores && fetchedStores.length > 0) {
                    const mappedStores = fetchedStores.map(store => ({
                        label: store.identifier,
                        value: store.store_id.toString(),
                        currency: store.setccurr,
                    }));
                    setStores(mappedStores);
                    setSelectedStore(mappedStores[0].value);
                } else {
                    showToast("No branch locations assigned.", "error");
                }
            } catch (err) {
                console.error("Failed to load initial data", err);
                showToast("Failed to load initial page data", "error");
            }
        };

        loadInitialData();
    }, [navigate]);

    // Fetch bank options whenever selected store changes
    useEffect(() => {
        if (!selectedStore) return;

        const loadBanksAndWallet = async () => {
            try {
                // const banks = await fetchBankList(selectedStore);

                const banks = await fetchPaystackBanksList(selectedStore);
                // console.log(banks)
                const options = (banks || []).map(b => ({
                    label: b.bank_name,
                    value: b.bank_code
                }));
                setBankOptions(options);

                const walletRes = await fetchPocketBalance(selectedStore);
                setWalletBalance(walletRes?.balance_amount || 0);

                // Fetch and map expense accounts
                const accounts = await fetchUtilExpenseAccounts();
                const mappedAccounts = (accounts || []).map(acc => ({
                    label: `${acc.account_id} — ${acc.name}`,
                    value: acc.account_id.toString()
                }));
                setExpenseAccounts(mappedAccounts);

                const orders = await fetchUtilClientProductionOrders(selectedStore);
                const mappedOrders = (orders || []).map(po => ({
                    label: po.description ? `#${po.productionorder_id} — ${po.description}` : `#${po.productionorder_id}`,
                    value: po.productionorder_id.toString()
                }));
                setProductionOrders(mappedOrders);
            } catch (err) {
                showToast(err.response?.data?.detail || "Failed to fetch bank list and wallet, or production orders for selected branch", "error");
            }
        };

        loadBanksAndWallet();
    }, [selectedStore]);

    // Dummy helper to simulate loading vendor credit/data
    const loadVendorCredit = (vendorId) => {
        // Can be tied to api helper if needed
    };

    // 2. Handle Vendor Selection & Pre-fill Bank Info if available
    const handleVendorSelect = (val) => {
        const vendorId = parseInt(val);
        const selected = vendors.find(v => v.vendor_id === vendorId);
        setVendorDetails(selected || null);
        setHeader(prev => ({ ...prev, vendor_id: vendorId }));

        // Reset previous account verification data
        setVerificationData(null);

        // Pre-fill bank details if attached to vendor record
        const vendorBankCode = selected?.bank_code || selected?.bank_account?.bank_code || '';
        const vendorAccNum = selected?.account_number || selected?.bank_account?.account_number || '';

        setBankCode(vendorBankCode);
        setAccountNumber(vendorAccNum);

        if (vendorAccNum && vendorAccNum.length === 10) {
            handleVerifyAccount(vendorAccNum, vendorBankCode);
        }

        if (vendorId) {
            loadVendorCredit(vendorId);
        }
    };

    // 3. Verify Account details via API (identical to Employee Profile verification flow)
    const handleVerifyAccount = async (numToVerify = accountNumber, bCode = bankCode) => {
        if (!bCode) {
            showToast("Please select a bank.", "error");
            return;
        }
        if (!numToVerify || numToVerify.length !== 10 || !/^\d{10}$/.test(numToVerify)) {
            showToast("Account number must be exactly 10 digits.", "error");
            return;
        }

        setVerifying(true);
        setVerificationData(null);
        try {
            const payload = {
                store_id: Number(selectedStore),
                bank_code: bCode,
                account_number: numToVerify,
            };
            const response = await client.postData('/payroll/verify-bank-with-paystack', payload);

            if (response) {
                setVerificationData(response);
                showToast("Account verified successfully!", "success");
            } else {
                showToast("Could not verify account details.", "error");
            }
        } catch (err) {
            showToast(err.response?.data?.detail || "Account verification failed.", "error");
        } finally {
            setVerifying(false);
        }
    };

    // 4a. Initiate Vendor Payment Transfer
    const handleInitiateVendorTransfer = async (e) => {
        e.preventDefault();

        if (!verificationData) {
            showToast("Please verify the vendor bank account before making a transfer.", "error");
            return;
        }
        if (!amount || Number(amount) <= 0) {
            showToast("Please enter a valid transfer amount.", "error");
            return;
        }
        if (Number(amount) > walletBalance) {
            showToast(`Insufficient balance. Current balance is ₦${walletBalance.toLocaleString()}`, "error");
            return;
        }

        setInitiating(true);
        try {
            const payload = {
                store_id: Number(selectedStore),
                vendor_id: header.vendor_id,
                amount: Number(amount),
                bank_code: bankCode,
                account_number: accountNumber,
                account_name: verificationData.account_name,
                idempotency_key: generateUniquePoNumber(),
                narration: narration || `Vendor Payment to ${vendorDetails?.vendorname}`
            };

            const res = await client.postData('/vendor/initiate-vendor-payment-from-wallet', payload);
            setTransferData(res);
            setIsOtpModalOpen(true);
            showToast("Vendor transfer initiated. OTP requested for confirmation.", "info");
        } catch (err) {
            showToast(err.response?.data?.detail || "Failed to initiate vendor transfer.", "error");
        } finally {
            setInitiating(false);
        }
    };

    // 4b. Initiate Daily Expense Transfer
    const handleInitiateDailyExpenseTransfer = async (e) => {
        e.preventDefault();

        if (!verificationData) {
            showToast("Please verify the recipient bank account before making a transfer.", "error");
            return;
        }
        if (!amount || Number(amount) <= 0) {
            showToast("Please enter a valid transfer amount.", "error");
            return;
        }
        if (Number(amount) > walletBalance) {
            showToast(`Insufficient balance. Current balance is ₦${walletBalance.toLocaleString()}`, "error");
            return;
        }

        setInitiating(true);
        try {
            const payload = {
                store_id: Number(selectedStore),
                amount: Number(amount),
                bank_code: bankCode,
                account_number: accountNumber,
                account_name: verificationData.account_name,
                narration: narration || 'Daily Expense Payment',
                idempotency_key: generateUniquePoNumber(),
                productionorder_id: isJobTied && selectedProductionOrderId ? Number(selectedProductionOrderId) : null
            };

            const res = await client.postData('/accounting/initiate-daily-expense-from-wallet', payload);
            setTransferData(res);
            setIsOtpModalOpen(true);
            showToast("Daily expense initiated. OTP requested for confirmation.", "info");
        } catch (err) {
            showToast(err.response?.data?.detail || "Failed to initiate daily expense transfer.", "error");
        } finally {
            setInitiating(false);
        }
    };

    // 5a. Confirm Vendor Execution using OTP
    const handleConfirmVendorOtp = async () => {
        if (!otpCode || otpCode.length < 4) {
            showToast("Please enter a valid OTP.", "error");
            return;
        }

        setConfirmingOtp(true);
        try {
            const payload = {
                payout_id: transferData?.payout_id,
                otp: otpCode,
                store_id: Number(selectedStore),
                vendor_id: header.vendor_id,
                amount: Number(amount),
                description: narration,
            };

            await client.postData('/vendor/confirm-vendor-payment-from-wallet', payload);
            showToast("Payment to vendor completed successfully!", "success");

            setIsOtpModalOpen(false);
            setOtpCode('');
            resetForm();
        } catch (err) {
            showToast(err.response?.data?.detail || "OTP Verification failed.", "error");
        } finally {
            setConfirmingOtp(false);
        }
    };

    // 5b. Confirm Daily Expense Execution using OTP
    const handleConfirmDailyExpenseOtp = async () => {
        if (!otpCode || otpCode.length < 4) {
            showToast("Please enter a valid OTP.", "error");
            return;
        }

        setConfirmingOtp(true);
        try {
            const payload = {
                payout_id: transferData?.payout_id,
                otp: otpCode,
                store_id: Number(selectedStore),
                amount: Number(amount),
                description: narration,
                debit_account_id: selectedExpenseAccount ? Number(selectedExpenseAccount) : null,
                productionorder_id: isJobTied && selectedProductionOrderId ? Number(selectedProductionOrderId) : null
            };

            // console.log(payload);
            await client.postData('/accounting/confirm-expense-payment-from-wallet', payload);
            showToast("Daily expense payment completed successfully!", "success");

            setIsOtpModalOpen(false);
            setOtpCode('');
            resetForm();
        } catch (err) {
            showToast(err.response?.data?.detail || "OTP Verification failed.", "error");
        } finally {
            setConfirmingOtp(false);
        }
    };

    return (
        <div className="p-4 sm:p-6 lg:p-8 bg-zinc-50 min-h-screen font-sans flex flex-col">
            <Card className="w-full max-w-5xl mx-auto flex-1 flex flex-col p-6 sm:p-8 shadow-xl rounded-xl bg-white border border-zinc-200">
                {/* Dashboard Header */}
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 border-b border-zinc-200 pb-5 mb-6">
                    <div>
                        <h1 className="text-2xl font-bold text-zinc-900 flex items-center gap-2">
                            <Wallet size={28} className="text-orange-600" /> Expenses & Transfers
                        </h1>
                        <p className="text-xs text-zinc-500 mt-1">
                            Manage supplier/vendor settlements and track business operational expenses.
                        </p>
                    </div>

                    <div className="w-full md:w-64">
                        <SearchableSelectBox
                            options={stores}
                            value={selectedStore}
                            onChange={(e) => {
                                setSelectedStore(e.target.value);
                                setVerificationData(null); // Clear previous account verification
                            }}
                            placeholder="Select branch / store..."
                        />
                    </div>
                </div>

                {/* Tab Navigation Controls */}
                <div className="flex border-b border-zinc-200 mb-6">
                    <button
                        className={`flex items-center gap-2 py-3 px-6 font-semibold text-sm border-b-2 transition-all ${activeTab === 'vendors'
                            ? 'border-blue-600 text-blue-600'
                            : 'border-transparent text-zinc-500 hover:text-zinc-800'
                            }`}
                        onClick={() => {
                            setActiveTab('vendors');
                            resetForm();
                        }}
                    >
                        <CreditCard size={18} />
                        Vendor Payments
                    </button>
                    <button
                        className={`flex items-center gap-2 py-3 px-6 font-semibold text-sm border-b-2 transition-all ${activeTab === 'daily'
                            ? 'border-orange-600 text-orange-600'
                            : 'border-transparent text-zinc-500 hover:text-zinc-800'
                            }`}
                        onClick={() => {
                            setActiveTab('daily');
                            resetForm();
                        }}
                    >
                        <Receipt size={18} />
                        Daily Expenses
                    </button>
                </div>

                {/* TAB 1: VENDOR PAYMENTS */}
                {activeTab === 'vendors' && (
                    <div className="space-y-6 max-w-3xl">
                        {/* Step 1: Select Vendor */}
                        <div className="grid grid-cols-1 mb-4">
                            <SearchableSelectBox
                                label="Select Vendor"
                                options={vendors.map(v => ({
                                    label: v.vendorname,
                                    value: v.vendor_id
                                }))}
                                value={header.vendor_id}
                                onChange={e => handleVendorSelect(e.target.value)}
                                required
                                icon={<Truck size={18} className="text-blue-600" />}
                            />

                            {/* Responsive Vendor Info Badge */}
                            {vendorDetails && (
                                <div className="mt-3 p-3 bg-blue-50 rounded-lg border border-blue-100 flex items-center gap-3">
                                    <div className="bg-blue-600 p-2 rounded-full text-white flex-shrink-0">
                                        <Info size={16} />
                                    </div>
                                    <div className="text-xs">
                                        <p className="font-bold text-blue-900 uppercase">Contact Person</p>
                                        <p className="text-blue-700">
                                            {vendorDetails.member?.addresses?.[0]?.first_name || 'N/A'} — {' '}
                                            {vendorDetails.member?.addresses?.[0]?.phone1 || 'No Phone'}
                                        </p>
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Step 2: Validate Account Details */}
                        {header.vendor_id && (
                            <div className="bg-zinc-50 p-4 rounded-xl border border-zinc-200 space-y-4">
                                <h3 className="text-xs font-bold text-zinc-800 uppercase tracking-wider flex items-center gap-1.5">
                                    <Building2 size={16} className="text-blue-600" /> Vendor Banking Details
                                </h3>

                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                    <SearchableSelectBox
                                        name="bank_code"
                                        label="Select Bank"
                                        value={bankCode}
                                        onChange={(e) => {
                                            setBankCode(e.target.value);
                                            setVerificationData(null);
                                        }}
                                        options={bankOptions}
                                        required
                                        placeholder="Search & select bank..."
                                    />

                                    <Input
                                        name="account_number"
                                        label="NUBAN Account Number"
                                        placeholder="10-digit account number"
                                        value={accountNumber}
                                        maxLength={10}
                                        onChange={(e) => {
                                            const val = e.target.value.replace(/\D/g, '').slice(0, 10);
                                            setAccountNumber(val);
                                            setVerificationData(null);

                                            if (val.length === 10) {
                                                handleVerifyAccount(val, bankCode);
                                            }
                                        }}
                                        required
                                    />
                                </div>

                                {verifying && (
                                    <div className="flex items-center gap-2 text-xs text-blue-600 font-medium">
                                        <Loader2 size={14} className="animate-spin" /> Verifying account details...
                                    </div>
                                )}

                                {verificationData && (
                                    <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-lg flex items-center justify-between">
                                        <div>
                                            <p className="text-[10px] text-emerald-600 font-bold uppercase">Verified Account Name</p>
                                            <p className="text-xs font-bold text-emerald-900">{verificationData.account_name}</p>
                                        </div>
                                        <ShieldCheck size={20} className="text-emerald-600" />
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Pocket Balance & Transfer Validation Info */}
                        {header.vendor_id && (
                            <div className="flex items-center justify-between p-3 bg-zinc-100 rounded-lg border border-zinc-200">
                                <span className="text-xs text-zinc-600 font-medium">Available Pocket Balance:</span>
                                <span className="text-sm font-bold text-zinc-900">
                                    ₦{walletBalance.toLocaleString()}
                                </span>
                            </div>
                        )}

                        {/* Step 3: Payment Transfer Form */}
                        {verificationData && (
                            <form onSubmit={handleInitiateVendorTransfer} className="bg-white p-4 rounded-xl border border-zinc-200 space-y-4">
                                <h3 className="text-xs font-bold text-zinc-800 uppercase tracking-wider flex items-center gap-1.5">
                                    <Send size={16} className="text-orange-600" /> Payment Details
                                </h3>

                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                    <Input
                                        label="Transfer Amount"
                                        type="number"
                                        placeholder="0.00"
                                        value={amount}
                                        onChange={(e) => setAmount(e.target.value)}
                                        required
                                    />

                                    <Input
                                        label="Narration / Description"
                                        placeholder="e.g. Payment for... or Invoice #1024 settlement"
                                        value={narration}
                                        required
                                        onChange={(e) => setNarration(e.target.value)}
                                    />
                                </div>

                                {Number(amount) > walletBalance && (
                                    <p className="text-xs font-semibold text-red-600">
                                        ⚠️ Transfer amount exceeds available pocket balance (₦{walletBalance.toLocaleString()}).
                                    </p>
                                )}

                                <Button
                                    type="submit"
                                    variant="indigo"
                                    disabled={initiating || Number(amount) > walletBalance}
                                    className="w-full sm:w-auto px-6 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white flex items-center justify-center gap-2"
                                >
                                    {initiating && <Loader2 size={16} className="animate-spin" />}
                                    Initiate Wallet Transfer
                                </Button>
                            </form>
                        )}
                    </div>
                )}

                {/* TAB 2: DAILY EXPENSES */}
                {activeTab === 'daily' && (
                    <div className="space-y-6 max-w-3xl">
                        {/* Step 1: Validate Account Details */}
                        <div className="bg-zinc-50 p-4 rounded-xl border border-zinc-200 space-y-4">
                            <h3 className="text-xs font-bold text-zinc-800 uppercase tracking-wider flex items-center gap-1.5">
                                <Building2 size={16} className="text-orange-600" /> Recipient Banking Details
                            </h3>

                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                <SearchableSelectBox
                                    name="bank_code"
                                    label="Select Bank"
                                    value={bankCode}
                                    onChange={(e) => {
                                        setBankCode(e.target.value);
                                        setVerificationData(null);
                                    }}
                                    options={bankOptions}
                                    required
                                    placeholder="Search & select bank..."
                                />

                                <Input
                                    name="account_number"
                                    label="NUBAN Account Number"
                                    placeholder="10-digit account number"
                                    value={accountNumber}
                                    maxLength={10}
                                    onChange={(e) => {
                                        const val = e.target.value.replace(/\D/g, '').slice(0, 10);
                                        setAccountNumber(val);
                                        setVerificationData(null);

                                        if (val.length === 10) {
                                            handleVerifyAccount(val, bankCode);
                                        }
                                    }}
                                    required
                                />
                            </div>

                            {verifying && (
                                <div className="flex items-center gap-2 text-xs text-orange-600 font-medium">
                                    <Loader2 size={14} className="animate-spin" /> Verifying account details...
                                </div>
                            )}

                            {verificationData && (
                                <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-lg flex items-center justify-between">
                                    <div>
                                        <p className="text-[10px] text-emerald-600 font-bold uppercase">Verified Account Name</p>
                                        <p className="text-xs font-bold text-emerald-900">{verificationData.account_name}</p>
                                    </div>
                                    <ShieldCheck size={20} className="text-emerald-600" />
                                </div>
                            )}
                        </div>

                        {/* Available Pocket Balance Display */}
                        <div className="flex items-center justify-between p-3 bg-zinc-100 rounded-lg border border-zinc-200">
                            <span className="text-xs text-zinc-600 font-medium">Available Pocket Balance:</span>
                            <span className="text-sm font-bold text-zinc-900">
                                ₦{walletBalance.toLocaleString()}
                            </span>
                        </div>

                        {/* Step 2: Payment Transfer Form */}
                        {verificationData && (
                            <form onSubmit={handleInitiateDailyExpenseTransfer} className="bg-white p-4 rounded-xl border border-zinc-200 space-y-4">
                                <h3 className="text-xs font-bold text-zinc-800 uppercase tracking-wider flex items-center gap-1.5">
                                    <Send size={16} className="text-orange-600" /> Expense Payment Details
                                </h3>

                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                    <Input
                                        label="Transfer Amount"
                                        type="number"
                                        placeholder="0.00"
                                        value={amount}
                                        onChange={(e) => setAmount(e.target.value)}
                                        required
                                    />

                                    <Input
                                        label="Narration / Purpose"
                                        placeholder="e.g. Office supplies / Utility payment"
                                        value={narration}
                                        required
                                        onChange={(e) => setNarration(e.target.value)}
                                    />

                                    <SearchableSelectBox
                                        label="Select Expense Account"
                                        options={expenseAccounts}
                                        value={selectedExpenseAccount}
                                        onChange={(e) => setSelectedExpenseAccount(e.target.value)}
                                        placeholder="Search & select expense account..."
                                        required
                                    />

                                    {/* Checkbox to Tie Expense to a Production Order / Job */}
                                    <div className="pt-2">
                                        <Checkbox
                                            id="tieToJob"
                                            label="Tie this expense to a specific job/production order?"
                                            checked={isJobTied}
                                            onChange={(e) => {
                                                setIsJobTied(e.target.checked);
                                                if (!e.target.checked) setSelectedProductionOrderId('');
                                            }}
                                            containerClassName="mb-2"
                                            labelClassName="text-xs font-semibold text-zinc-700"
                                        />

                                        {isJobTied && (
                                            <div className="mt-2">
                                                <SearchableSelectBox
                                                    label="Select Production Order / Job"
                                                    options={productionOrders}
                                                    value={selectedProductionOrderId}
                                                    onChange={(e) => setSelectedProductionOrderId(e.target.value)}
                                                    placeholder="Search & select job order..."
                                                    required={isJobTied}
                                                />
                                            </div>
                                        )}
                                    </div>
                                </div>

                                {Number(amount) > walletBalance && (
                                    <p className="text-xs font-semibold text-red-600">
                                        ⚠️ Transfer amount exceeds available pocket balance (₦{walletBalance.toLocaleString()}).
                                    </p>
                                )}

                                <Button
                                    type="submit"
                                    variant="indigo"
                                    disabled={initiating || Number(amount) > walletBalance}
                                    className="w-full sm:w-auto px-6 bg-orange-600 hover:bg-orange-700 disabled:opacity-50 text-white flex items-center justify-center gap-2"
                                >
                                    {initiating && <Loader2 size={16} className="animate-spin" />}
                                    Initiate Expense Transfer
                                </Button>
                            </form>
                        )}
                    </div>
                )}
            </Card>

            {/* Step 4 & 5: OTP Confirmation Modal */}
            <Modal
                isOpen={isOtpModalOpen}
                onClose={() => setIsOtpModalOpen(false)}
                title={activeTab === 'vendors' ? "Confirm Vendor Transfer" : "Confirm Daily Expense Transfer"}
                size="md"
            >
                <div className="space-y-4">
                    <p className="text-xs text-zinc-600">
                        An authorization OTP code has been generated. Please enter the OTP to confirm the transfer of{' '}
                        <span className="font-bold text-zinc-900">{amount}</span> to{' '}
                        <span className="font-bold text-zinc-900">{verificationData?.account_name}</span>.
                    </p>

                    <Input
                        label="OTP Code"
                        placeholder="Enter OTP"
                        value={otpCode}
                        onChange={(e) => setOtpCode(e.target.value)}
                        required
                    />

                    <div className="pt-4 flex justify-end gap-2 border-t border-zinc-100">
                        <Button variant="ghost" size="sm" onClick={() => setIsOtpModalOpen(false)}>
                            Cancel
                        </Button>

                        <Button
                            variant="indigo"
                            size="sm"
                            onClick={activeTab === 'vendors' ? handleConfirmVendorOtp : handleConfirmDailyExpenseOtp}
                            disabled={confirmingOtp || !otpCode}
                            className="flex items-center gap-1.5 bg-emerald-600 hover:bg-emerald-700 text-white"
                        >
                            {confirmingOtp ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle2 size={14} />}
                            Confirm & Execute Payment
                        </Button>
                    </div>
                </div>
            </Modal>
        </div>
    );
};

export default WalletExpenses;